/*
  idleLogout.js
  - Starts an inactivity timer (1 hour).
  - Shows a warning modal at 55 minutes (5 minutes before logout).
  - Calls the backend logout endpoint (with credentials) when idle timeout hits.
  - Exposes start/stop/reset functions and a React hook to mount globally.
*/

import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Box, Typography } from "@mui/material";

const IDLE_MS = 60 * 60 * 1000; // 1 hour
const WARNING_MS = 5 * 60 * 1000; // 5 minutes

// const IDLE_MS = 10000; // 10 seconds
// const WARNING_MS = 5000; // 5 seconds

let idleTimer = null;
let warningTimer = null;

function clearTimers() {
  if (idleTimer) {
    clearTimeout(idleTimer);
    idleTimer = null;
  }
  if (warningTimer) {
    clearTimeout(warningTimer);
    warningTimer = null;
  }
}

function startTimers(onIdle, onWarn) {
  clearTimers();
  // warning at IDLE_MS - WARNING_MS
  if (typeof onWarn === "function") {
    warningTimer = setTimeout(() => {
      try {
        onWarn();
      } catch (e) {
        console.error("onWarn error:", e);
      }
    }, Math.max(0, IDLE_MS - WARNING_MS));
  }
  idleTimer = setTimeout(() => {
    try {
      onIdle();
    } catch (e) {
      console.error("onIdle error:", e);
    }
  }, IDLE_MS);
}

function resetTimers(onIdle, onWarn) {
  startTimers(onIdle, onWarn);
}

function stopTimers() {
  clearTimers();
}

// Helper to call logout via sendBeacon if available else fetch
async function callLogout() {
  try {
    const url = process.env.REACT_APP_API_URL + "/logout";
    // Try sendBeacon first (best-effort, works when page unloads)
    if (navigator.sendBeacon) {
      // sendBeacon accepts a Blob or string; server doesn't need body
      const sent = navigator.sendBeacon(url);
      if (sent) return;
    }
    // Fallback to fetch with credentials
    await fetch(url, { method: "POST", credentials: "include" });
  } catch (err) {
    console.error("Idle logout failed:", err);
  }
}

// React hook that provides the warning modal and wires up listeners
export function useIdleLogout({ onIdle } = {}) {
  const [openWarning, setOpenWarning] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [countdown, setCountdown] = useState(Math.floor(WARNING_MS / 1000));
  const countdownRef = useRef();
  const onIdleRef = useRef(onIdle || (async () => {
    // Default idle action: call logout and redirect to root
    await callLogout();
    window.location.href = "/";
  }));

  useEffect(() => {
    // no global setter required; onIdleRef is already initialized from props
    const activity = () => {
      // On any activity, hide warning and reset timers
      setOpenWarning(false);
      setShowSnackbar(false);
      setCountdown(Math.floor(WARNING_MS / 1000));
      resetTimers(onIdleRef.current, () => setShowSnackbar(true));
    };

    // Set timers with onIdle callback and onWarn to toggle snackbar/modal
    startTimers(onIdleRef.current, () => {
      setShowSnackbar(true);
      setCountdown(Math.floor(WARNING_MS / 1000));
    });

    // Listen for activity
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
    events.forEach((evt) => window.addEventListener(evt, activity, { passive: true }));
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") activity();
    });

    return () => {
      stopTimers();
      const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
      events.forEach((evt) => window.removeEventListener(evt, activity));
      // cleanup if necessary (none required)
    };
  }, []);

  // Countdown effect for the inactivity box
  useEffect(() => {
    if (!showSnackbar) return;
    setCountdown(Math.floor(WARNING_MS / 1000));
    countdownRef.current && clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          setShowSnackbar(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      clearInterval(countdownRef.current);
    };
  }, [showSnackbar]);

  const staySignedIn = () => {
    // User clicked to stay signed in: hide warning and reset timers
    setOpenWarning(false);
    setShowSnackbar(false);
    resetTimers(onIdleRef.current, () => setShowSnackbar(true));
  };

  const logoutNow = async () => {
    setOpenWarning(false);
    await callLogout();
    window.location.href = "/";
  };

  const WarningModal = () => (
    <>
      <Dialog open={openWarning} onClose={() => setOpenWarning(false)}>
        <DialogTitle>Session Expiring</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You've been inactive for a while. For your security, you'll be logged out in 5 minutes. Would you like to stay signed in?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={logoutNow} color="secondary">Logout Now</Button>
          <Button onClick={staySignedIn} color="primary" autoFocus>Stay Signed In</Button>
        </DialogActions>
      </Dialog>

      {/* Centered inactivity alert box with countdown */}
      {showSnackbar && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            pointerEvents: 'none',
            background: 'rgba(251, 241, 226, 0.7)', // theme.palette.main.primary with opacity
          }}
        >
          <Box
            sx={{
              px: { xs: 2, sm: 4 },
              py: { xs: 2, sm: 3 },
              minWidth: 320,
              maxWidth: '92vw',
              background: 'linear-gradient(120deg, #FBF1E2 0%, #fffbe8 100%)', // theme.palette.main.primary
              color: '#7B6E66', // theme.palette.text.secondary
              borderRadius: 16,
              fontWeight: 600,
              fontSize: { xs: '1rem', sm: '1.2rem' },
              boxShadow: '0 8px 32px 0 #C9A77744',
              textAlign: 'center',
              letterSpacing: 1,
              border: '2px solid #C9A777', // theme.palette.button.primary
              fontFamily: 'Montserrat, Inter, Arial, sans-serif',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              transition: 'all 0.3s cubic-bezier(.4,2,.6,1)',
              pointerEvents: 'auto',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
              <span role="img" aria-label="timer" style={{ fontSize: '2em', filter: 'drop-shadow(0 2px 6px #C9A77788)' }}>⏰</span>
              <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', letterSpacing: 1, fontSize: '1.3em', marginLeft: 8, color: '#945800' }}>{countdown}s</span>
            </Box>
            <Typography variant="body2" sx={{ color: '#7B6E66', fontWeight: 500, fontSize: { xs: '1.08rem', sm: '1.18rem' }, mt: 0.5, display: 'block', textShadow: '0 2px 8px #FBF1E2', lineHeight: 1.6, letterSpacing: 0.2 }}>
              You're inactive. You'll be logged out soon.<br/>
              <Button onClick={staySignedIn} variant="contained" size="small" sx={{ mt: 2, pointerEvents: 'auto', background: '#C9A777', color: '#fff', fontWeight: 600, borderRadius: 2, boxShadow: '0 2px 8px #C9A77733', '&:hover': { background: '#b4966b' } }}>
                Stay Signed In
              </Button>
            </Typography>
          </Box>
        </Box>
      )}
    </>
  );

  return { WarningModal };
}

const idleLogoutUtils = {
  startTimers,
  resetTimers,
  stopTimers,
  callLogout,
};

export default idleLogoutUtils;
